<template>
	<v-card>
		<v-card-title class="d-flex align-center">
			<v-icon
				icon="$mobile"
				start
			/>
			<translate>History insured product for Order</translate>#{{ deviceHistory.order_key }}
		</v-card-title>
		<v-card-text>
			<div v-if="deviceHistory.device_history !== undefined && deviceHistory.device_history.length > 0">
				<v-data-table
					:headers="headers"
					:items="deviceHistory.device_history"
					hide-default-footer
				>
					<template #item.refurbished="{ item }">
						<span v-if="item.refurbished">
							<translate>Yes</translate>
						</span>
						<span v-else>
							<translate>No</translate>
						</span>
					</template>
				</v-data-table>
				<br>
			</div>
			<div>
				<h5><translate>New Device</translate></h5>
				<hr>
				<device-search
					v-model="deviceId"
					@ean-active="EANActive"
					@ean-state="EANStateChange"
				/>
				<v-row>
					<v-col
						cols="3"
					>
						<v-text-field
							v-model="imei"
							variant="outlined"
							:label="$gettext('IMEI')"
						/>
					</v-col>
					<v-col
						v-if="!eanActive"
						cols="3"
					>
						<v-text-field
							v-model="ean"
							variant="outlined"
							:label="$gettext('EAN')"
							@keyup="$emit('update:ean', ean)"
						/>
					</v-col>
					<v-col cols="3">
						<v-text-field
							v-model="serialNumber"
							variant="outlined"
							:label="$gettext('Serial Number')"
						/>
					</v-col>
					<v-col cols="3">
						<date-picker
							v-model="startDate"
							name="start_date"
							:label="$gettext('Start Date')"
							:min="minDate"
							:max="maxDate"
							@input="getStartDate"
						/>
					</v-col>
					<v-col cols="3">
						<v-text-field
							v-model="replacementReason"
							variant="outlined"
							:label="$gettext('Reason for Replacement')"
						/>
					</v-col>
					<v-col
						v-if="!eanActive"
					>
						<v-checkbox
							v-model="refurbished"
							label="Refurbished"
						/>
					</v-col>

					<div
						v-if="updateLoading"
						class="text-center loader"
					>
						<div class="load">
							<v-progress-circular
								indeterminate
								color="primary"
							/>
						</div>
					</div>
					<v-col
						v-else
						xs2
					>
						<v-btn
							color="success"
							@click="changeDevice"
						>
							<translate>Save</translate>
						</v-btn>
					</v-col>
				</v-row>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>
import axios from 'axios';
import {
	displayError, inform,
} from 'laravel-front-library';

import DeviceSearch from '../molecules/DeviceSearch.vue';

export default {
	components: {
		DeviceSearch,
	},
	props: {
		orderId: {
			type: Number,
			required: true,
		},

		orderrowId: {
			type: Number,
			required: true,
		},
	},

	emits: ['update:ean',],

	data () {
		return {
			devices: [],
			deviceId: null,
			byEan: false,
			byName: false,
			deviceName: '',
			startDate: new Date().toISOString().substr(0, 'YYYY-MM-DD'.length),
			minDate: undefined,
			maxDate: undefined,
			deviceHistory: [],
			headers: '',
			findDeviceLoading: false,
			updateLoading: false,
			ean: '',
			eanActive: false,
			imei: '',
			serialNumber: '',
			replacementReason: '',
			searchInput: '',
			searchLoading: false,
			refurbished: false,
		};
	},

	watch: {
		/**
		 * @param {string} search
		 */
		searchInput (search) {
			if (search && !this.deviceId) {
				this.searchDevices(search);
			}
		},
	},

	mounted () {
		this.$nextTick(() => {
			this.setHeaders();
		});

		this.updateDeviceHistory();
	},

	methods: {

		displayError,

		inform,

		/**
		 * To set the headers.
		 *
		 * @returns {void}
		 */
		setHeaders () {
			this.headers = [
				{
					title: this.$gettext('Device'),
					value: 'full_name',
				},
				{
					title: this.$gettext('IMEI'),
					value: 'imei',
				},
				{
					title: this.$gettext('EAN'),
					value: 'ean',
				},
				{
					title: this.$gettext('Serial Number'),
					value: 'serial_number',
				},
				{
					title: this.$gettext('Start Date'),
					value: 'start',
				},
				{
					title: this.$gettext('End Date'),
					value: 'end',
				},
				{
					title: 'Refurbished',
					value: 'refurbished',
					sortable: false,
				},
				{
					title: this.$gettext('Reason for Replacement'),
					value: 'reason',
					sortable: false,
				},
			];
		},

		/**
		 * @param {boolean} param
		 */
		EANActive (param) {
			this.eanActive = param;
		},

		/**
		 * @param {string} value
		 */
		EANStateChange (value) {
			this.ean = value;
		},

		/**
		 * To set the user's selected start date as the startDate.
		 *
		 * @param {string} val - the new start date value
		 * @returns {void}
		 */
		getStartDate (val) {
			this.startDate = val;
		},

		/**
		 * Formats dates received from backend into ISO 8601 strings required by the DatePicker component
		 * In other words:
		 *
		 * Receives: 31-01-2020
		 * Returns: 2020-01-31
		 *
		 * This is done manually instead of using a Date object because javascript doesn't like dd-mm-yyyy formatted
		 * dates.
		 *
		 * @param {string} dateString - Date received from backend
		 * @returns {string} - the formatted date
		 */
		formatDate (dateString) {
			const year = dateString.match(/\d{4}/);
			const month = dateString.match(/-\d{2}-/);
			const day = dateString.match(/\d{2}/);

			return year + month + day;
		},

		/**
		 * Get minimum date allowed for start date of device
		 * Date string is ISO 8601 formatted
		 *
		 * @returns {void}
		 */
		updateMinDate () {
			const history = this.deviceHistory.device_history;

			this.minDate = this.formatDate(history[history.length - 1].start);
		},

		/**
		 * Get maximum date allowed for start date of device
		 * Date string is ISO 8601 formatted
		 *
		 * @returns {void}
		 */
		updateMaxDate () {
			const history = this.deviceHistory.device_history;

			this.maxDate = this.formatDate(history[history.length - 1].end);
		},


		/**
		 * To call the endpoint which gives all useful information to display the device
		 * change history.
		 *
		 * @returns {void}
		 */
		updateDeviceHistory () {
			axios
				.get(`/bo/orders/history?order_id=${this.orderId}&orderrow_id=${this.orderrowId}`)
				.then((response) => {
					this.deviceHistory = response.data;
					// Update dates allowed in the date picker
					this.updateMaxDate();
					this.updateMinDate();
				})
				.catch((error) => {
					window.captureException(error);
				});
		},

		/**
		 * To reset data when the user clicks findDeviceByEAN button.
		 *
		 * @returns {void}
		 */
		findDeviceByEan () {
			this.byEan = true;
			this.byName = false;
			this.reset();
		},

		/**
		 * To reset data when the user clicked search device button.
		 *
		 * @returns {void}
		 */
		searchDevice () {
			this.byName = true;
			this.byEan = false;
			this.reset();
		},

		/**
		 * To reset data.
		 *
		 * @returns {void}
		 */
		reset () {
			this.deviceName = '';
			this.devices = [];
			this.deviceId = null;
			this.searchLoading = false;
		},

		/**
		 * To call the endpoint which creates a new device change.
		 *
		 * @returns {void}
		 */
		changeDevice () {
			this.updateLoading = true;

			const dataToSend = JSON.stringify({
				'imei': this.imei,
				'start_date': this.startDate,
				'device_id': this.deviceId,
				'reason_for_replacement': this.replacementReason,
				'order_id': this.orderId,
				'orderrow_id': this.orderrowId,
				'serial_number': this.serialNumber,
				'ean': this.ean,
				'refurbished': this.refurbished,
			});

			axios
				.post('/bo/orders/device', dataToSend)
				.then((response) => {
					this.inform(response.data);
					this.updateDeviceHistory();
					this.updateLoading = false;
				})
				.catch((error) => {
					this.displayError(error.response.data);
					this.updateLoading = false;
				});
		},
	},
};
</script>
